import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import Pagination from './common/Pagination';
import usePagination from './common/usePagination';
import './KpiSection.css';

/**
 * KpiSection
 * ==========
 * Non-functional-requirement (NFR) KPIs shown on every team dashboard, right
 * after the Zafran Remediation Tickets section:
 *   1. Performance          - API response time (target <= 2s)
 *   2. Availability & Reliability - server-side API Uptime (target 99.9%)
 *   3. Scalability          - estimated concurrent users (target >= 100)
 *
 * All three are derived from the team's real per-account CloudWatch metrics,
 * collected daily (last 24 hours) by the backend KPI collector:
 *   - Performance   = collective API/response latency (API Gateway primary)
 *   - Availability  = 1 - 5xx / requests (collective, API Gateway primary)
 *   - Scalability   = Little's-Law estimate of concurrent users (target 100)
 *
 * SLA attainment (per-KPI % of measured days that met target, plus an overall
 * roll-up) is promoted to a headline business figure above the detail chart.
 */

const API_URL = getApiUrl();

// Date-range presets for the KPI time-range selector. Module-scope so the
// reference is stable across renders (safe to use inside useMemo without deps).
const RANGE_PRESETS = [
  { key: '24h', label: 'Last 24 hours', days: 1 },
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '6m', label: 'Last 6 months', days: 180 },
];

const STATUS_META = {
  met: { label: 'Meets target', cls: 'kpi-status-met', icon: '\u2705' },
  ok: { label: 'Healthy', cls: 'kpi-status-met', icon: '\u2705' },
  at_risk: { label: 'At risk', cls: 'kpi-status-atrisk', icon: '\u26a0\ufe0f' },
  breach: { label: 'Target not met', cls: 'kpi-status-breach', icon: '\u274c' },
  pending: { label: 'Awaiting data', cls: 'kpi-status-pending', icon: '\u2014' },
};

const statusMeta = (status) => STATUS_META[status] || STATUS_META.pending;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Format a YYYY-MM-DD day key as e.g. "31 Aug 2026".
const formatDay = (ymd) => {
  if (!ymd) return '';
  const parts = ymd.split('-');
  if (parts.length !== 3) return ymd;
  const [y, m, d] = parts;
  const idx = parseInt(m, 10) - 1;
  return idx >= 0 && idx < 12 ? `${parseInt(d, 10)} ${MONTHS[idx]} ${y}` : ymd;
};

// Short axis label, e.g. "31 Aug".
const formatDayShort = (ymd) => {
  if (!ymd) return '';
  const parts = ymd.split('-');
  if (parts.length !== 3) return ymd;
  const [, m, d] = parts;
  const idx = parseInt(m, 10) - 1;
  return idx >= 0 && idx < 12 ? `${parseInt(d, 10)} ${MONTHS[idx]}` : ymd;
};

const KpiSection = ({ team }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded] = useState(true);
  // Date-range selection. Default is LAST 24 HOURS (the most recent day).
  // Preset ranges filter the ~6-month stored history client-side.
  const [range, setRange] = useState('24h');

  const fetchKpi = useCallback(async () => {
    if (!team) return;
    setLoading(true);
    setError(null);
    try {
      const axios = (await import('axios')).default;
      const res = await axios.get(`${API_URL}${API_ENDPOINTS.kpi(team)}`);
      setData(res.data || null);
    } catch (err) {
      console.error('Error fetching KPIs:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, [team]);

  // Load the KPI data (all values are server-derived from CloudWatch).
  useEffect(() => {
    fetchKpi();
  }, [fetchKpi]);

  // Scalability (estimated concurrent users) is hidden from the UI: the
  // estimate is unreliable (can be ~0 / negative) and not meaningful to show.
  // Drop it from the card definitions here so it never renders.
  const definitions = (data?.definitions || []).filter((d) => d.key !== 'scalability');
  const fullHistory = useMemo(() => (Array.isArray(data?.history) ? data.history : []), [data]);

  // Apply the selected date range to the full ~6-month stored history.
  const history = useMemo(() => {
    if (!fullHistory.length) return [];
    const sorted = [...fullHistory].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    const preset = RANGE_PRESETS.find((p) => p.key === range) || RANGE_PRESETS[0];
    const n = preset.days || 1;
    return sorted.slice(-n);
  }, [fullHistory, range]);

  // "Current" reflects the most recent day within the selected range.
  const current = useMemo(() => (history.length ? history[history.length - 1] : (data?.current || null)), [history, data]);

  // Label describing the active range, shown next to the selector.
  const rangeLabel = useMemo(
    () => (RANGE_PRESETS.find((p) => p.key === range) || RANGE_PRESETS[0]).label,
    [range]
  );

  // Daily Detail rows (newest first). Paginate with the SAME DynamoDB-style
  // pagination used by the Zafran JIRA table - 5 rows per page - but only when
  // the selected range is more than 24 hours (24h has a single row). Reset to
  // page 1 whenever the range changes.
  const detailRows = useMemo(
    () => [...history].slice(-90).reverse(),
    [history]
  );
  const isPaginated = range !== '24h';
  const DETAIL_PAGE_SIZE = 5;
  const {
    page: detailPage,
    setPage: setDetailPage,
    pageCount: detailPageCount,
    pageItems: detailPageItems,
    totalItems: detailTotal,
    pageSize: detailPageSize,
  } = usePagination(detailRows, DETAIL_PAGE_SIZE, [range]);
  // When not paginating (24h), show every row; otherwise show the current page.
  const detailVisibleRows = isPaginated ? detailPageItems : detailRows;

  // ---- Business-value roll-ups (computed client-side from the selected range) ----
  // SLA attainment: % of measured days that MET each KPI target over the window.
  const slaSummary = useMemo(() => {
    const perfDays = history.filter((d) => d.performance?.loadTimeSeconds != null);
    const availDays = history.filter((d) => d.availability?.uptimePercent != null);

    const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : null);

    const perfMet = perfDays.filter((d) => d.performance.loadTimeSeconds <= 2).length;
    const availMet = availDays.filter((d) => d.availability.uptimePercent >= 99.9).length;

    // Scalability (concurrent-users estimate) is intentionally omitted from the
    // SLA roll-up and the "% of days target met" chart.
    return [
      { key: 'performance', label: 'Performance ≤ 2s', met: perfMet, total: perfDays.length, pct: pct(perfMet, perfDays.length), fill: '#0051A3' },
      { key: 'availability', label: 'Uptime ≥ 99.9%', met: availMet, total: availDays.length, pct: pct(availMet, availDays.length), fill: '#22c55e' },
    ];
  }, [history]);

  // Overall SLA attainment = mean of the per-KPI attainment percentages that
  // are defined (not pending). Reuses the exact slaSummary.pct math; when every
  // per-KPI value is pending the overall stays pending (never a fabricated 0/100).
  const overallSla = useMemo(() => {
    const defined = slaSummary.map((s) => s.pct).filter((v) => v != null);
    if (!defined.length) return null;
    return Math.round(defined.reduce((a, b) => a + b, 0) / defined.length);
  }, [slaSummary]);

  // Build the "current value" + status for a KPI card. The value shown is the
  // most recent day within the selected range; the sub-label names that day so
  // it stays accurate whether the range is 24h or 6 months.
  const asOf = current?.date ? `as of ${formatDayShort(current.date)}` : 'collected daily';
  const cardValue = (key) => {
    if (key === 'performance') {
      // Real server-side API response time (from CloudWatch), in seconds.
      const seconds = current?.performance?.loadTimeSeconds;
      const status = seconds == null
        ? 'pending'
        : (seconds <= 2 ? 'met' : (seconds <= 3 ? 'at_risk' : 'breach'));
      return {
        display: seconds == null ? '—' : `${seconds.toFixed(2)}s`,
        sub: seconds == null ? 'collected daily' : `API response time · ${asOf}`,
        status,
      };
    }
    if (key === 'availability') {
      const a = current?.availability;
      return {
        display: a?.uptimePercent == null ? '—' : `${a.uptimePercent.toFixed(2)}%`,
        sub: a?.uptimePercent == null ? 'collected daily' : asOf,
        status: a?.status || 'pending',
      };
    }
    return { display: '—', sub: '', status: 'pending' };
  };

  const historyValue = (month, key) => {
    if (key === 'performance') {
      const v = month?.performance?.loadTimeSeconds;
      return { text: v == null ? '—' : `${v.toFixed(2)}s`, status: month?.performance?.status || 'pending' };
    }
    if (key === 'availability') {
      const v = month?.availability?.uptimePercent;
      return { text: v == null ? '—' : `${v.toFixed(2)}%`, status: month?.availability?.status || 'pending' };
    }
    const v = month?.scalability?.peakConcurrent;
    return { text: v == null ? '—' : `${v}`, status: month?.scalability?.status || 'pending' };
  };

  const overall = data?.currentStatus || 'pending';
  const overallMeta = statusMeta(overall);

  return (
    <section className="section-container kpi-section">
      <h3 className="section-title">
        📐 KPIs / Non-Functional Requirements
        <span className={`kpi-overall-badge ${overallMeta.cls}`} title="Overall KPI status this month">
          {overallMeta.icon} {overallMeta.label}
        </span>
      </h3>

      {expanded && (
        <>
          {/* Date-range selector. Defaults to Last 24 hours; can view any range
              up to the ~6 months of stored daily history. */}
          <div className="kpi-range-bar">
            <span className="kpi-range-label">📅 Time range:</span>
            <div className="kpi-range-buttons">
              {RANGE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`kpi-range-btn ${range === p.key ? 'active' : ''}`}
                  onClick={() => setRange(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <span className="kpi-range-active">Showing: {rangeLabel}</span>
          </div>

          {loading && !data ? (
            <div className="kpi-loading"><div className="spinner-small" /><span>Loading KPIs…</span></div>
          ) : error && !data ? (
            <div className="kpi-error">
              <span>⚠️ {error}</span>
              <button onClick={fetchKpi}>Retry</button>
            </div>
          ) : (
            <>
              <div className="kpi-cards-grid">
                {definitions.map((def) => {
                  const { display, sub, status } = cardValue(def.key);
                  const meta = statusMeta(status);
                  return (
                    <div key={def.key} className={`kpi-card ${meta.cls}`}>
                      <div className="kpi-card-header">
                        <span className="kpi-card-icon">{def.icon}</span>
                        <span className="kpi-card-title">{def.title}</span>
                        <span className={`kpi-badge ${meta.cls}`}>{meta.icon} {meta.label}</span>
                      </div>
                      <div className="kpi-card-metric">
                        <span className="kpi-metric-value">{display}</span>
                        <span className="kpi-metric-name">{def.metric}{sub ? ` · ${sub}` : ''}</span>
                      </div>
                      <div className="kpi-card-target">🎯 {def.targetLabel}</div>
                      <p className="kpi-card-definition">{def.definition}</p>
                    </div>
                  );
                })}
              </div>

              {/* Headline SLA attainment: per-KPI % + overall % across the three.
                  Reuses slaSummary.pct (met over measured days); pending when a
                  KPI has zero measured days in the range (never 0%/100%). */}
              <div className="kpi-sla-headline" data-testid="kpi-sla-headline">
                <h4 className="kpi-history-title">🎯 SLA Attainment ({rangeLabel})</h4>
                <div className="kpi-sla-headline-tiles">
                  <div className="kpi-sla-tile kpi-sla-tile-overall" data-testid="sla-overall">
                    <span className="kpi-sla-tile-label">Overall SLA</span>
                    <span className="kpi-sla-tile-value">
                      {overallSla == null ? '—' : `${overallSla}%`}
                    </span>
                    <span className="kpi-sla-tile-sub">
                      {overallSla == null ? 'Awaiting data' : `across ${slaSummary.filter((s) => s.pct != null).length} KPIs · ${rangeLabel}`}
                    </span>
                  </div>
                  {slaSummary.map((s) => (
                    <div key={s.key} className="kpi-sla-tile" data-testid={`sla-tile-${s.key}`}>
                      <span className="kpi-sla-tile-label" style={{ color: s.fill }}>{s.label}</span>
                      <span className="kpi-sla-tile-value">
                        {s.pct == null ? '—' : `${s.pct}%`}
                      </span>
                      <span className="kpi-sla-tile-sub">
                        {s.pct == null ? 'Awaiting data' : `${s.met}/${s.total} days met`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>


              {/* Detail table for the selected range */}
              <div className="kpi-history">
                <h4 className="kpi-history-title">Daily Detail ({rangeLabel})</h4>
                {history.length === 0 ? (
                  <p className="kpi-history-empty">No data for the selected range. Data is collected daily; try a wider range.</p>
                ) : (
                  <table className="kpi-history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>⚡ API Response Time</th>
                        <th>🟢 Uptime</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailVisibleRows.map((d) => {
                        const perf = historyValue(d, 'performance');
                        const avail = historyValue(d, 'availability');
                        return (
                          <tr key={d.date}>
                            <td className="kpi-history-month">{formatDay(d.date)}</td>
                            <td className={statusMeta(perf.status).cls}>{perf.text}</td>
                            <td className={statusMeta(avail.status).cls}>{avail.text}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                {isPaginated && history.length > 0 && (
                  <Pagination
                    page={detailPage}
                    pageCount={detailPageCount}
                    totalItems={detailTotal}
                    pageSize={detailPageSize}
                    onPageChange={setDetailPage}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default KpiSection;
